/**
 * AGGREGATE Query Configuration Component
 */

import * as React from 'react';
import { 
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Input,
  ScrollArea,
  toast
} from '@/components/ui';
import { 
  Check,
  X,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { 
  AggregateOperationEnum,
  AggregateQueryOptions,
  AggregateClause,
  SortDirectionEnum
} from '@/lib/engine';
import { AggregateQueryConfigProps, getValidAggregationsForColumn } from './types';
import { FilterBuilder } from './filter-builder';
import { 
  useAggregateStore, 
  getHavingColumns,
  isColumnInGroupBy,
  hasAggregateOperation
} from './aggregate-query-store';
import { SortColumns, SortableColumn } from './sort-columns';

/**
 * AGGREGATE Query Configuration Component
 */
export function AggregateQueryConfig({ query, originalColumns = [], updateQuery }: AggregateQueryConfigProps) {
  // Initialize state hooks first to ensure they're always rendered
  const [columnFilter, setColumnFilter] = React.useState('');
  const [aggregateFilter, setAggregateFilter] = React.useState('');
  const [showAdvancedFilter, setShowAdvancedFilter] = React.useState(false);
  
  // Initialize the aggregate store
  const { dispatch, actions } = useAggregateStore(query, updateQuery);
  
  // Initialize these regardless of render condition to avoid hook rule violations
  const emptyArray: any[] = [];
  
  // For filteredColumns - use empty array when not rendered
  const filteredColumns = React.useMemo(() => {
    // Check if we should render the component
    if (query.type !== 'AGGREGATE') return emptyArray;
    
    if (!columnFilter) return originalColumns;
    
    const lowerFilter = columnFilter.toLowerCase();
    return originalColumns.filter((col: any) => 
      col.name.toLowerCase().includes(lowerFilter) || 
      col.schema.type.toString().toLowerCase().includes(lowerFilter)
    );
   
  }, [originalColumns, columnFilter, query.type]);
  
  // For filteredAggregates - use empty array when not rendered
  const filteredAggregates = React.useMemo(() => {
    // Check if we should render the component
    if (query.type !== 'AGGREGATE') return emptyArray;
    
    const options = query.options as AggregateQueryOptions;
    if (!options.aggregates) return emptyArray;
    
    if (!aggregateFilter) return options.aggregates;
    
    const lowerFilter = aggregateFilter.toLowerCase();
    return options.aggregates.filter(agg => 
      agg.column.name.toLowerCase().includes(lowerFilter) || 
      agg.operation.toLowerCase().includes(lowerFilter) ||
      agg.alias.toLowerCase().includes(lowerFilter)
    );
   
  }, [aggregateFilter, query]);
  
  // For havingColumns - use empty array when not rendered
  const havingColumns = React.useMemo(() => {
    // Check if we should render the component
    if (query.type !== 'AGGREGATE') return emptyArray;
    
    const options = query.options as AggregateQueryOptions;
    if (!options.groupBy || !options.aggregates) return emptyArray;
    
    return getHavingColumns(options.groupBy, options.aggregates);
   
  }, [query]);
  
  // For sortableColumns - use empty array when not rendered
  const sortableColumns = React.useMemo(() => {
    // Check if we should render the component
    if (query.type !== 'AGGREGATE') return emptyArray;
    
    const options = query.options as AggregateQueryOptions;
    if (!options.groupBy || !options.aggregates) return emptyArray;
    
    const groupByColumns = options.groupBy.map(col => ({
      ...col,
      displayName: col.name
    }));
    
    const aggregateColumns = options.aggregates.map(agg => ({
      name: agg.alias,
      schema: agg.column.schema,
      displayName: agg.alias
    }));
    
    return [...groupByColumns, ...aggregateColumns];
   
  }, [query]);
  
  // Check if we should render the component
  if (query.type !== 'AGGREGATE') return null;
  
  const options = query.options as AggregateQueryOptions;
  
  // Initialize options.groupBy and options.aggregates if they're undefined
  if (!options.groupBy) {
    options.groupBy = [];
  }
  
  if (!options.aggregates) {
    options.aggregates = [];
  }
  
  // Initialize options.orderBy if it's undefined
  if (!options.orderBy) {
    options.orderBy = [];
  }
  
  // Function to toggle advanced filter mode
  const toggleAdvancedFilter = (isAdvanced: boolean) => {
    setShowAdvancedFilter(isAdvanced);
  };
  
  // Check if a column is already in the ORDER BY clause
  const getColumnSortDirection = (column: any) => {
    const orderByItem = options.orderBy?.find(item => 
      item.column.name === column.name
    );
    
    return orderByItem ? orderByItem.direction : null;
  };
  
  // Render the column list with groupBy options
  const renderColumnList = () => {
    return (
      <div className="space-y-4 h-full flex flex-col">
        <div className="flex items-center gap-2 shrink-0">
          <Input
            placeholder="Search columns..."
            value={columnFilter}
            onChange={(e) => setColumnFilter(e.target.value)}
            className="text-xs"
          />
        </div>
        
        <ScrollArea className="flex-1">
          <div className="space-y-1 pr-4">
            {filteredColumns.map((column: any, index: number) => {
              const isGrouped = isColumnInGroupBy(options, column);
              // Get only the allowed aggregation operations for this column type
              const validAggregations = getValidAggregationsForColumn(column);
              
              return (
                <div 
                  key={index} 
                  className="flex flex-col p-2 rounded-md hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium">{column.name}</div>
                      <span className="text-xs text-muted-foreground mt-auto font-light">
                        {column.schema.type.toString()}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1 flex-wrap justify-end">
                      {/* Group By Button */}
                      <Button
                        variant={isGrouped ? "default" : "outline"}
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => dispatch(actions.toggleGroupBy(column))}
                      >
                        {isGrouped ? (
                          <>
                            <Check className="h-3 w-3 mr-1" />
                            Group by
                          </>
                        ) : (
                          "Group by"
                        )}
                      </Button>
                      
                      {/* Only show valid aggregation operations for this specific column type */}
                      {!isGrouped && validAggregations.map((op) => {
                        const isActive = hasAggregateOperation(options, column, op);
                        // Convert to sentence case
                        const opDisplay = op.charAt(0) + op.slice(1).toLowerCase();
                        
                        return (
                          <Button
                            key={op}
                            variant={isActive ? "default" : "outline"}
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => dispatch(actions.toggleAggregate(column, op))}
                          >
                            {isActive && <Check className="h-3 w-3 mr-1" />}
                            {opDisplay}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {filteredColumns.length === 0 && (
              <div className="text-center p-4 text-sm text-muted-foreground">
                No columns match your search
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    );
  };
  
  // Render the aggregate list
  const renderAggregateList = () => {
    return (
      <div className="space-y-4 h-full flex flex-col">
        <div className="flex items-center gap-2 shrink-0">
          <Input
            placeholder="Search aggregates..."
            value={aggregateFilter}
            onChange={(e) => setAggregateFilter(e.target.value)}
            className="text-xs"
          />
        </div>
        
        <ScrollArea className="flex-1">
          <div className="space-y-1 pr-4">
            {filteredAggregates.map((agg, index) => (
              <div 
                key={index} 
                className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="text-xs font-medium">
                    {agg.operation.charAt(0) + agg.operation.slice(1).toLowerCase()}({agg.column.name})
                  </div>
                  <span className="text-[0.5rem] text-muted-foreground font-light">
                    {agg.column.schema.type.toString()}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    <span className="text-xs mr-2">Alias:</span>
                    <Input
                      value={agg.alias}
                      onChange={(e) => dispatch(actions.updateAggregateAlias(index, e.target.value))}
                      className="h-7 text-xs w-[120px]"
                    />
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive"
                    onClick={() => dispatch(actions.removeAggregate(index))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            {options.aggregates.length === 0 && (
              <div className="text-center p-4 text-sm text-muted-foreground">
                No aggregates defined. Add some from the Columns tab.
              </div>
            )}
            
            {options.aggregates.length > 0 && filteredAggregates.length === 0 && (
              <div className="text-center p-4 text-sm text-muted-foreground">
                No aggregates match your search
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    );
  };
  
  // Render the sort list
  const renderSortList = () => {
    return (
      <SortColumns
        columns={sortableColumns}
        sortedColumns={options.orderBy || []}
        onToggleSort={(column) => dispatch(actions.addOrderBy(column))}
      />
    );
  };
  
  return (
    <div className="h-full flex flex-col">
      <Tabs defaultValue="columns" className="w-full h-full flex flex-col">
        <TabsList className="flex w-full">
          <TabsTrigger className="flex-1" value="columns">Columns</TabsTrigger>
          <TabsTrigger className="flex-1" value="aggregates">
            Aggregates
            {options.aggregates.length > 0 && (
              <Badge variant="secondary" className="ml-2">{options.aggregates.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger className="flex-1" value="filters">Filters</TabsTrigger>
          <TabsTrigger className="flex-1" value="sort">
            Sort
            {options.orderBy && options.orderBy.length > 0 && (
              <Badge variant="secondary" className="ml-2">{options.orderBy.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="columns" className="flex-1 overflow-hidden p-2">
          {renderColumnList()}
        </TabsContent>
        
        <TabsContent value="aggregates" className="flex-1 overflow-hidden p-2">
          {renderAggregateList()}
        </TabsContent>
        
        <TabsContent value="filters" className="flex-1 overflow-auto p-2">
          <div className="space-y-6">
            {/* Where Clause */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium">Where</div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => toggleAdvancedFilter(!showAdvancedFilter)}
                >
                  {showAdvancedFilter ? "Simple Mode" : "Advanced Mode"}
                </Button>
              </div>
              <div className="text-xs text-muted-foreground">
                Filter rows before aggregation. Use SQL WHERE syntax.
              </div>
              <FilterBuilder
                columns={originalColumns}
                whereClause={options.where || ''}
                onWhereChange={(where) => dispatch(actions.updateWhereClause(where))}
                advancedPlaceholder="e.g. price > 100 AND category = 'Electronics'"
                advancedHelpText="Filter rows before aggregation using SQL WHERE syntax"
                showAdvancedFilter={showAdvancedFilter}
                onAdvancedFilterChange={toggleAdvancedFilter}
                showAdvancedToggle={false}
              />
            </div>
            
            {/* Having Clause */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium">Having</div>
              </div>
              <div className="text-xs text-muted-foreground">
                Filter groups after aggregation. Use SQL HAVING syntax.
              </div>
              <FilterBuilder
                columns={havingColumns}
                whereClause={options.having || ''}
                onWhereChange={(having) => dispatch(actions.updateHavingClause(having))}
                advancedPlaceholder="e.g. COUNT(*) > 5 OR SUM(price) > 1000"
                advancedHelpText="Filter groups after aggregation using SQL HAVING syntax"
                showAdvancedToggle={false}
                showAdvancedFilter={showAdvancedFilter}
              />
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="sort" className="flex-1 overflow-auto p-2">
          {renderSortList()}
        </TabsContent>
      </Tabs>
    </div>
  );
} 